import React, { useState, useEffect } from 'react';
import { X, Key, Shield, AlertTriangle, Save, Loader2 } from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { showError, showSuccess } from '../../../utils/toast';

interface DBCredentialModalProps {
    isOpen: boolean;
    onClose: () => void;
    dbType: string;
    agent_id: string;
    asset_id: string;
    instance_id: string;
    port: number;
}

interface FormField {
    key: string;
    type: string;
    default: any;
    required: boolean;
    label: string;
}

interface SchemaResponse {
    status: boolean;
    db_type: string;
    fields: FormField[];
    required_fields: string[];
    security_notice: string;
}

const DBCredentialModal: React.FC<DBCredentialModalProps> = ({
    isOpen,
    onClose,
    dbType,
    agent_id,
    asset_id,
    instance_id,
    port,
}) => {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [schema, setSchema] = useState<SchemaResponse | null>(null);
    const [credentialName, setCredentialName] = useState('');
    const [formData, setFormData] = useState<Record<string, any>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentCredentialId, setCurrentCredentialId] = useState<number | null>(null);

    useEffect(() => {
        if (isOpen && dbType) {
            checkExistingCredentials();
        } else {
            // Reset state on close
            setSchema(null);
            setFormData({});
            setErrors({});
            setCredentialName('');
            setIsEditMode(false);
            setCurrentCredentialId(null);
        }
    }, [isOpen, dbType]);

    const checkExistingCredentials = async () => {
        setLoading(true);
        try {
            // 1. Check for existing credentials
            const credParams = {

                asset_id: Number(asset_id),
                instance_id: instance_id

            };
            const credRes = await baseapi.get('/db_monitor/credentials/', { params: credParams });

            let existingCred = null;
            if (credRes.data.status && credRes.data.data?.length > 0) {
                existingCred = credRes.data.data[0];
                setIsEditMode(true);
                setCurrentCredentialId(existingCred.id);
                setCredentialName(existingCred.credential_name);
            }

            // 2. Fetch schema
            const schemaRes = await baseapi.get(`/db_monitor/credentials/schema/${dbType}`);
            if (schemaRes.data.status) {
                setSchema(schemaRes.data);

                const initialData: Record<string, any> = {};
                let existingData: Record<string, any> = {};

                if (existingCred?.credential_data) {
                    try {
                        existingData = typeof existingCred.credential_data === 'string'
                            ? JSON.parse(existingCred.credential_data)
                            : existingCred.credential_data;
                    } catch (e) {
                        console.error("Failed to parse credential_data", e);
                    }
                }

                schemaRes.data.fields.forEach((field: FormField) => {
                    // Use existing value if available, else default
                    if (existingData[field.key] !== undefined) {
                        initialData[field.key] = existingData[field.key];
                        // If it's a password and we're editing, use '*' as suggested
                        if (field.type === 'password') {
                            initialData[field.key] = '*';
                        }
                    } else {
                        initialData[field.key] = field.default;
                    }
                });
                setFormData(initialData);
            }
        } catch (error: any) {
            showError(error.message || "Failed to load credential information");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (key: string, value: any) => {
        setFormData(prev => ({ ...prev, [key]: value }));
        if (errors[key]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[key];
                return newErrors;
            });
        }
    };

    const validate = () => {
        const newErrors: Record<string, string> = {};
        if (!credentialName.trim()) {
            newErrors.credential_name = "Credential name is required";
        }
        if (schema) {
            schema.fields.forEach(field => {
                if (field.required && (formData[field.key] === undefined || formData[field.key] === "")) {
                    newErrors[field.key] = `${field.label} is required`;
                }
            });
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        try {
            if (isEditMode && currentCredentialId) {
                const payload = {
                    credential_name: credentialName,
                    credential_data: formData
                };
                const response = await baseapi.put(`/db_monitor/credentials/${currentCredentialId}`, payload);
                if (response.data.status) {
                    showSuccess("Credential updated successfully");
                    onClose();
                }
            } else {
                const payload = {
                    agent_id: Number(agent_id),
                    asset_id: Number(asset_id),
                    instance_id,
                    db_type: dbType,
                    port,
                    credential_name: credentialName,
                    credential_data: formData
                };
                const response = await baseapi.post('/db_monitor/credentials/', payload);
                if (response.data.status) {
                    showSuccess("Credential added successfully");
                    onClose();
                }
            }
        } catch (error: any) {
            showError(error.message || `Failed to ${isEditMode ? 'update' : 'add'} credential`);
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]" onClick={onClose} />
            <div className="fixed inset-0 flex items-center justify-center z-[9999] p-4">
                <div className="w-full max-w-xl bg-gray-900 border border-orange-400/20 shadow-2xl rounded-xl text-white relative max-h-[90vh] overflow-hidden flex flex-col">

                    {/* Header */}
                    <div className="bg-gray-900 border-b border-gray-800 px-6 py-4 flex justify-between items-center shrink-0">
                        <div className="flex items-center gap-2">
                            <Key className="w-5 h-5 text-orange-400" />
                            <h2 className="text-lg font-bold uppercase tracking-tight">{isEditMode ? 'Edit' : 'Add'} DB Credential</h2>
                        </div>
                        <button onClick={onClose} className="text-gray-400 hover:text-white p-1 rounded-full hover:bg-gray-800 transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto custom-scroll p-6 space-y-6">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-2" />
                                <p className="text-sm text-gray-400 font-medium uppercase tracking-widest">Loading Schema...</p>
                            </div>
                        ) : schema ? (
                            <>
                                {/* Security Notice */}
                                <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3 flex gap-3 items-start">
                                    <Shield className="w-4 h-4 text-blue-400 mt-1 shrink-0" />
                                    <p className="text-[11px] text-gray-200 font-medium italic leading-relaxed">
                                        {schema.security_notice}
                                    </p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                                    {/* Credential Name */}
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Credential Name *</label>
                                        <input
                                            type="text"
                                            autoComplete="off"
                                            className={`w-full bg-gray-800 border ${errors.credential_name ? 'border-red-500' : 'border-gray-700'} rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none transition-colors`}
                                            value={credentialName}
                                            onChange={(e) => setCredentialName(e.target.value)}
                                            placeholder="Enter credential name"
                                        />
                                        {errors.credential_name && <p className="text-red-500 text-[10px] mt-1 font-bold italic">{errors.credential_name}</p>}
                                    </div>

                                    {/* Dynamic Fields */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {schema.fields.map((field) => (
                                            <div key={field.key} className={field.type === 'boolean' || field.key === 'database' ? 'md:col-span-2' : ''}>
                                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                                                    {field.label} {field.required && '*'}
                                                </label>

                                                {field.type === 'boolean' ? (
                                                    <div className="flex gap-4 p-2 bg-gray-800/50 rounded-lg border border-gray-700">
                                                        <label className="flex items-center gap-2 cursor-pointer group">
                                                            <input
                                                                type="radio"
                                                                name={field.key}
                                                                checked={formData[field.key] === true}
                                                                onChange={() => handleInputChange(field.key, true)}
                                                                className="w-3 h-3 accent-orange-500"
                                                            />
                                                            <span className="text-xs text-gray-300 group-hover:text-white">True</span>
                                                        </label>
                                                        <label className="flex items-center gap-2 cursor-pointer group">
                                                            <input
                                                                type="radio"
                                                                name={field.key}
                                                                checked={formData[field.key] === false}
                                                                onChange={() => handleInputChange(field.key, false)}
                                                                className="w-3 h-3 accent-orange-500"
                                                            />
                                                            <span className="text-xs text-gray-300 group-hover:text-white">False</span>
                                                        </label>
                                                    </div>
                                                ) : field.type === 'password' ? (
                                                    <input
                                                        type="password"
                                                        autoComplete="new-password"
                                                        className={`w-full bg-gray-800 border ${errors[field.key] ? 'border-red-500' : 'border-gray-700'} rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none`}
                                                        value={formData[field.key] || ''}
                                                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                                                    />
                                                ) : (
                                                    <input
                                                        type={field.type === 'integer' ? 'number' : 'text'}
                                                        autoComplete="off"
                                                        className={`w-full bg-gray-800 border ${errors[field.key] ? 'border-red-500' : 'border-gray-700'} rounded-lg px-3 py-2 text-sm focus:border-orange-400 focus:outline-none`}
                                                        value={formData[field.key] === undefined ? '' : formData[field.key]}
                                                        onChange={(e) => handleInputChange(field.key, field.type === 'integer' ? Number(e.target.value) : e.target.value)}
                                                    />
                                                )}
                                                {errors[field.key] && <p className="text-red-500 text-[10px] mt-1 font-bold italic">{errors[field.key]}</p>}
                                            </div>
                                        ))}
                                    </div>
                                </form>
                            </>
                        ) : (
                            <div className="text-center py-12 text-gray-500 italic">
                                <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-20" />
                                <p>Failed to load schema for {dbType}</p>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-900 border-t border-gray-800 px-6 py-4 flex justify-end gap-3 shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-white transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={submitting || !schema}
                            className="px-6 py-2 bg-orange-600 rounded-lg text-[11px] font-bold uppercase tracking-widest flex items-center gap-2 hover:bg-orange-700 transition-all active:scale-95 disabled:opacity-50 disabled:pointer-events-none shadow-lg shadow-orange-600/20"
                        >
                            {submitting ? <Loader2 className="animate-spin w-4 h-4" /> : <Save className="w-4 h-4" />}
                            {isEditMode ? 'Update' : 'Save'} Credential
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DBCredentialModal;
